import type { AxiosError } from 'axios';
import { t, te } from '../i18n';
import { useToast } from '../composables/useToast';
import { useNotice } from '../composables/useDialog';

// Unified API-error surfacing (notices rollout — Documents/lumi/ux/notices).
// Never renders the raw backend string: maps status/code to a LOCALIZED
// errors.* key, and prefers a non-blocking toast-with-retry for transient
// failures (self-healing UI). Each error reaches the user through exactly ONE
// channel — a toast OR a blocking dialog, never both — and never exposes an
// internal correlation/error id.

/**
 * Map an API failure to a localized errors.* key. Falls back to errors.generic.
 *
 * `isApiFailure` distinguishes "the server did not answer" from "we could not read
 * the answer": it defaults to `true` so every existing direct caller (which only
 * ever dealt with real AxiosErrors) keeps its behavior unchanged. A caller working
 * from an `unknown` thrown value (see `parseApiErrorEnvelope`) passes `false` for
 * anything that is not Axios-shaped, so a status-less non-API throw — a bug in our
 * own code, not a transport failure — maps to `errors.unexpected` instead of being
 * laundered into "check your internet connection".
 */
export function errorKeyFor(status?: number, code?: string, isApiFailure = true): string {
  if (code && te(`errors.codes.${code}`)) return `errors.codes.${code}`;
  const s = status ?? 0;
  if (s === 0 && !isApiFailure) return 'errors.unexpected';
  // 408 means the server gave up waiting for OUR request to arrive — a slow or
  // broken link on the user's side, so "check your connection" is honest. 504 is
  // the opposite: our gateway reached the user fine and gave up waiting for the
  // service behind it, so it must not blame the user's internet. 502/503 stay on
  // errors.server — a service down or restarting, not a timeout.
  if (s === 0 || s === 408) return 'errors.network';
  if (s === 504) return 'errors.timeout';
  switch (s) {
    case 400:
    case 422:
      return 'errors.validation';
    case 401:
      return 'errors.unauthorized';
    case 403:
      return 'errors.forbidden';
    case 404:
      return 'errors.notFound';
    case 409:
      return 'errors.conflict';
    case 429:
      return 'errors.rateLimited';
  }
  if (s >= 500) return 'errors.server';
  return 'errors.generic';
}

/**
 * One offending field, as the ADR 0036 envelope reports it. `path` is the dotted
 * property path the server rejected (`exception.filter.ts` joins a ZodError's
 * `issue.path`), `message` is the backend's DEVELOPER-facing string.
 */
export interface ApiFieldIssue {
  path: string;
  message: string;
}

/** The facts the seam needs off a thrown value, whether or not it is an AxiosError. */
export interface ApiErrorEnvelope {
  status?: number;
  /** ADR 0036 structured code. Absent on the degradation path (see below). */
  code?: string;
  /** The envelope's `errors[]`, normalized. Empty when the response carried none. */
  issues: ApiFieldIssue[];
  /**
   * True when the thrown value is Axios-shaped (`isAxiosError: true`, or a
   * `response`/`request`/`config` property — the same duck-typing axios itself
   * uses). False for a value that plainly never touched the network, such as a
   * `TypeError` thrown by our own rendering code. Only a `true` value may map a
   * missing status to `errors.network` — see `errorKeyFor`.
   */
  isApiFailure: boolean;
}

/** Duck-types an AxiosError without importing the runtime check (browser + SSR safe). */
function isAxiosErrorShaped(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { isAxiosError?: unknown; response?: unknown; request?: unknown; config?: unknown };
  return e.isAxiosError === true || 'response' in e || 'request' in e || 'config' in e;
}

/**
 * Read `{ status, code, errors[] }` off anything that was thrown.
 *
 * Every NestJS module API answers a validation failure with the canonical envelope
 * `{ status: 400, message: 'Validation failed', code: 'VALIDATION_FAILED',
 *    errors: [{ path, message }] }` (ats-api/src/common/exception.filter.ts).
 *
 * Two live sources do NOT, and both are deliberate, not defects:
 *  - `user-api` is plain Express (the sanctioned ADR 0046 exception) and answers
 *    `{ message }` only — no `code`, no `errors[]`;
 *  - a direct object-storage PUT (a presigned upload) never passes through a Lumi
 *    exception filter at all, and may not even be an AxiosError.
 * Both land here with `code: undefined` and `issues: []` — the DEGRADATION path.
 * Callers must express that as one FORM-LEVEL message; an empty `issues` array is
 * "the server did not say which field", never "there were no errors".
 */
export function parseApiErrorEnvelope(error: unknown): ApiErrorEnvelope {
  const response = (error as { response?: { status?: number; data?: unknown } } | null)?.response;
  const data = response?.data;
  const body = data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
  const rawCode = body?.code;
  const rawIssues = body?.errors;
  const issues: ApiFieldIssue[] = Array.isArray(rawIssues)
    ? rawIssues
        .map((issue) => {
          const i = (issue ?? {}) as { path?: unknown; message?: unknown };
          return {
            path: typeof i.path === 'string' ? i.path : '',
            message: typeof i.message === 'string' ? i.message : '',
          };
        })
        .filter((issue) => issue.path !== '')
    : [];
  const isApiFailure = isAxiosErrorShaped(error);
  if (!isApiFailure) {
    // Not a transport failure — a bug in our own code threw this. Log it here, at
    // the seam, so it does not vanish silently the way a rendered "no connection"
    // banner would suggest. Only the error's own name/message/stack, never the
    // thrown value itself: an unknown throw could in principle carry arbitrary
    // (including personal) data, and the house rule is metadata only.
    const asError = error instanceof Error ? error : undefined;
    // eslint-disable-next-line no-console
    console.error('[apiError] non-API failure reached the API-error seam', {
      name: asError?.name ?? typeof error,
      message: asError?.message,
      stack: asError?.stack,
    });
  }
  return {
    status: typeof response?.status === 'number' ? response.status : undefined,
    code: typeof rawCode === 'string' ? rawCode : undefined,
    issues,
    isApiFailure,
  };
}

/**
 * The single localized string for a failure: `errorKeyFor(status, code)`, with a
 * curated `errors.codes.*` entry overriding it where one exists.
 *
 * The backend's own `message` is NEVER rendered (ADR 0036 rule 5) — it is a
 * developer-facing fallback, is not translated, and is sometimes German.
 */
export function apiErrorMessage(error: unknown): string {
  const { status, code, isApiFailure } = parseApiErrorEnvelope(error);
  return t(errorKeyFor(status, code, isApiFailure));
}

/** The one code a stale edit-form save is refused with (ADR 0101). */
export const RECORD_CHANGED = 'RECORD_CHANGED';

/**
 * Whether a failed save was refused because the record changed since the form
 * loaded it (ADR 0101: 409 `{ code: 'RECORD_CHANGED', currentUpdatedAt }`).
 * Takes either a `useApiMutation` failure outcome (it carries `code`) or the
 * thrown value itself.
 */
export function isRecordChanged(outcomeOrError: unknown): boolean {
  if (!outcomeOrError || typeof outcomeOrError !== 'object') return false;
  const direct = (outcomeOrError as { code?: unknown }).code;
  if (direct === RECORD_CHANGED) return true;
  if (!('response' in outcomeOrError) && !('error' in outcomeOrError)) return false;
  const thrown = 'error' in outcomeOrError ? (outcomeOrError as { error: unknown }).error : outcomeOrError;
  const data = (thrown as { response?: { data?: unknown } } | null)?.response?.data;
  return !!data && typeof data === 'object' && (data as { code?: unknown }).code === RECORD_CHANGED;
}

/**
 * `path -> localized message` for the fields the server rejected.
 *
 * The envelope's `errors[]` supplies WHICH fields are at fault; the string shown to
 * the user is the localized one, for the reason above. Per-field `errors.codes.*`
 * entries are explicitly not the mechanism (ADR 0036 rule 2 calls that a regression),
 * and no per-issue code exists to key one on. The first message per path wins, so a
 * duplicated path cannot silently overwrite the field the user is looking at.
 */
export function fieldErrorsFrom(error: unknown): Record<string, string> {
  const { status, code, issues } = parseApiErrorEnvelope(error);
  if (issues.length === 0) return {};
  const message = t(errorKeyFor(status, code));
  const out: Record<string, string> = {};
  for (const issue of issues) {
    if (!(issue.path in out)) out[issue.path] = message;
  }
  return out;
}

// Only idempotent/safe methods may be auto-retried.
const IDEMPOTENT = new Set(['get', 'head', 'options', 'put', 'delete']);

// A transient failure may be retried at most twice. After the second retry the
// problem is clearly not self-healing, so we stop offering retry and apologize +
// point the user at support instead.
//
// No UI call site currently opts in (`useApiMutation`'s `retryable` option,
// `useApiMutation.ts:249`, is `mayRetry ? retry : undefined` and nothing in the
// suite sets `retryable: true` on a real mutation — issue 195). The retry and
// exhaustion branches below are live code, reachable the moment a call site
// opts in, but nobody currently is: do not assume a user sees a retry button.
const MAX_RETRIES = 2;

/** Ask the host app (its AppChrome listens) to open its support entry point. */
function openSupport() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('lumi:open-support'));
  }
}

/** Transient + safe-to-retry: network/timeout/5xx/429 on an idempotent method. */
export function isRetryable(error: AxiosError): boolean {
  const s = error.response?.status ?? 0;
  const method = (error.config?.method ?? 'get').toLowerCase();
  const transient = s === 0 || s === 408 || s === 429 || s >= 500;
  return transient && IDEMPOTENT.has(method);
}

/** A hard/inconsistent failure that warrants a blocking dialog. */
function isHard(error: AxiosError): boolean {
  const s = error.response?.status ?? 0;
  return s === 409 || s >= 500;
}

/**
 * Global fallback surface for an API error. One error, one surface:
 *  - transient + retries left → toast with a retry button (re-issues the call);
 *  - transient + retries exhausted (2 tries) → apology toast + "contact support";
 *  - hard/inconsistent → blocking dialog with a short title + a descriptive body;
 *  - ordinary client error → non-blocking sticky toast.
 * Retry attempts are tracked on the request config so they persist across the
 * re-issued requests.
 */
export function surfaceApiError(error: AxiosError, retry?: () => unknown) {
  const status = error.response?.status;
  const code = (error.response?.data as { code?: string } | undefined)?.code;
  // Same distinction as `apiErrorMessage`/`parseApiErrorEnvelope`: a caller here may
  // have handed over something that was never actually an AxiosError (see
  // `useApiMutation`'s `attempt()`, which casts whatever was thrown) — do not tell
  // that user their connection is the problem.
  const message = t(errorKeyFor(status, code, isAxiosErrorShaped(error)));
  const { error: toastError } = useToast();
  const { error: noticeError } = useNotice();

  const cfg = error.config as (typeof error.config & { _retryCount?: number }) | undefined;
  const attempts = cfg?._retryCount ?? 0;

  if (retry && isRetryable(error) && attempts < MAX_RETRIES) {
    toastError(message, {
      retry: () => {
        if (cfg) cfg._retryCount = attempts + 1;
        void retry();
      },
    });
  } else if (retry && isRetryable(error)) {
    // Tried MAX_RETRIES times and still failing — stop retrying, apologize, and
    // route the user to support.
    toastError(t('errors.exhausted'), {
      action: openSupport,
      actionLabel: t('errors.contactSupport'),
    });
  } else if (isHard(error)) {
    noticeError({ title: t('errors.failedTitle'), body: message });
  } else {
    toastError(message);
  }
}

/**
 * A failed READ whose view has no error surface of its own (an option list behind a
 * form, a secondary panel): one sticky error toast, titled "could not be loaded", with
 * the translated cause. Never the backend text, never a second channel — a view with a
 * visible error line sets that instead (`unless: true` says so at the call site).
 *
 * One at a time: while a load-failed toast is still open — whatever its message — a
 * further call does nothing, so a fan-out of parallel loads, a loop or a router guard
 * re-running during an outage raises ONE toast, not one per request.
 */
export function surfaceLoadError(error: unknown, opts: { unless?: boolean } = {}) {
  if (opts.unless) return;
  const toast = useToast();
  const title = t('errors.loadFailedTitle');
  if (toast.toasts.value.some((x) => x.variant === 'error' && x.title === title)) return;
  toast.error(apiErrorMessage(error), { title });
}
